import React, { useState, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import axios from 'axios';
import {
Box,
Container,
Typography,
Avatar,
Paper,
Divider,
Chip,
Button,
Tabs,
Tab,
Grid,
Card,
CardContent,
CardHeader,
useMediaQuery,
useTheme,
Fade,
Slide,
IconButton,
Stack,
List,
ListItem,
ListItemIcon,
ListItemText,
Tooltip,
LinearProgress,
Accordion,
AccordionSummary,
AccordionDetails
} from '@mui/material';
import {
ArrowBack,
Person,
Description,
Groups,
School,
CalendarToday,
Share,
Bookmark,
BookmarkBorder,
Download,
Link as LinkIcon,
OpenInNew,
Science,
Business,
Email,
Phone,
ArrowBack as ArrowBackIcon,
Bookmark as BookmarkIcon,
Download as DownloadIcon,
ExpandMore as ExpandMoreIcon,
Factory,
Verified,
CheckCircle,
TramRounded
} from '@mui/icons-material';
import { APIResearcherList, APIUniversitiesList } from '../../utilities/APIS';
import { daysPassed, getIdCategory, getIdFullName } from '../../utilities/Functions';
import NavigationMenu from '../navigation/NavigationMenu';
import { motion, AnimatePresence } from 'framer-motion';
import { headers } from '../../utilities/Env';
import LoadingState from '../../components/LoadingState';

const ProductDetails = () => {
const theme = useTheme();
const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
const location = useLocation();
const navigate = useNavigate();

const [researchersData, setResearchersData] = useState([]);
const [universitiesData, setUniversitiesData] = useState([]);
const [tabValue, setTabValue] = useState(0);
const [loading, setLoading] = useState(true);
const [bookmarked, setBookmarked] = useState(false);
const [expandedSections, setExpandedSections] = useState({});

const { state } = location;
const {
    researcherName,
    uploadDate,
    universityName,
    productTitle,
    productDescription,
    productDetails,
    composition,
    manufacturer,
    tradeMark,
    patent
} = state || {};

useEffect(() => {
    loadData();
}, []);

const loadData = async () => {
    try {
    setLoading(true);
    const [researchersRes, uniRes] = await Promise.all([
        axios.get(APIResearcherList, { headers }),
        axios.get(APIUniversitiesList, { headers })
    ]);

    setResearchersData(researchersRes.data.data || []);
    setUniversitiesData(uniRes.data.data || []);
    setLoading(false);
    } catch (err) {
    setLoading(false);
    }
};

const handleTabChange = (event, newValue) => {
    setTabValue(newValue);
};

const handleBookmark = () => {
    setBookmarked(!bookmarked);
};

const handleShare = () => {
    navigator.clipboard.writeText(window.location.href);
};

const handleDownload = () => {
    // Implement download functionality
};

const goBack = () => {
    navigate('/products');
};

const handleSectionExpand = (section) => {
    setExpandedSections(prev => ({
        ...prev,
        [section]: !prev[section]
    }));
};

// Parse product details safely
const parsedProductDetails = productDetails ? JSON.parse(productDetails) : [];

// Get researcher contact info if available
const getResearcherContactInfo = () => {
    if (!researcherName || !researchersData.length) return null;
    
    const researcher = researchersData.find(res => 
        res.id.toString() === researcherName.toString()
    );
    
    return researcher || null;
};

const researcherInfo = getResearcherContactInfo();

const hasTechnicalDetails = () => {
    return composition || manufacturer || tradeMark || patent;
};

const getTechnicalDetailsCount = () => {
    let count = 0;
    if (composition) count++;
    if (manufacturer) count++;
    if (tradeMark) count++;
    if (patent) count++;
    return count;
};

if (loading) {
    return (
        <>
            <NavigationMenu />
            <LoadingState/>
        </>
    );
}

return (
    <Box  sx={{ display: 'flex', flexDirection: 'column', minHeight: '100vh' }}>
    <NavigationMenu />

    <Container mContainer maxWidth="lg" sx={{ py: 3, flex: 1, marginTop: 8 }}>
        {/* Header Section */}
        <Slide direction="down" in={true}>
        <Paper
            elevation={3}
            sx={{
            p: 3,
            mb: 3,
            borderRadius: 3,
            background: `linear-gradient(135deg, ${theme.palette.secondary.main} 0%, ${theme.palette.secondary.dark} 100%)`,
            color: 'white'
            }}
        >
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: 2 }}>
            <Box sx={{ flex: 1 }}>
                <Typography variant="h4" component="h1" fontWeight="bold" gutterBottom>
                {productTitle || 'Product Title'}
                </Typography>
                <Typography variant="body1" sx={{ opacity: 0.9, mb: 2 }}>
                {productDescription?.substring(0, 150) || 'Product description not available.'}
                {productDescription?.length > 150 && '...'}
                </Typography>
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 2 }}>
                <Chip 
                    icon={<School />}
                    label={getIdCategory(universityName, universitiesData) || 'University'}
                    variant="filled"
                    sx={{ 
                    backgroundColor: 'rgba(255,255,255,0.2)',
                    color: 'white',
                    backdropFilter: 'blur(10px)'
                    }}
                />
                <Chip
                    icon={<CalendarToday />}
                    label={`Published ${daysPassed(uploadDate)} days ago`}
                    variant="filled"
                    sx={{ 
                    backgroundColor: 'rgba(255,255,255,0.2)',
                    color: 'white',
                    backdropFilter: 'blur(10px)'
                    }}
                />
                {tradeMark && (
                    <Chip
                        icon={<TramRounded />}
                        label={tradeMark}
                        variant="filled"
                        sx={{ 
                            backgroundColor: 'rgba(255,255,255,0.2)',
                            color: 'white',
                            backdropFilter: 'blur(10px)'
                        }}
                    />
                )}
                {hasTechnicalDetails() && (
                    <Chip
                        icon={<CheckCircle />}
                        label={`${getTechnicalDetailsCount()} Technical Details`}
                        variant="filled"
                        sx={{ 
                            backgroundColor: 'rgba(255,255,255,0.2)',
                            color: 'white',
                            backdropFilter: 'blur(10px)'
                        }}
                    />
                )}
                </Box>
            </Box>
            
            <Box sx={{ display: 'flex', gap: 1, flexDirection: { xs: 'row', sm: 'column' } }}>
                <Tooltip title="Bookmark">
                <IconButton onClick={handleBookmark} sx={{ color: 'white' }}>
                    <BookmarkIcon color={bookmarked ? "secondary" : "inherit"} />
                </IconButton>
                </Tooltip>
                <Tooltip title="Share">
                <IconButton onClick={handleShare} sx={{ color: 'white' }}>
                    <Share />
                </IconButton>
                </Tooltip>
                <Button
                    variant="contained"
                    sx={{
                        bgcolor: 'white',
                        color: theme.palette.secondary.main,
                        fontWeight: 'bold',
                        '&:hover': {
                            bgcolor: 'rgba(255,255,255,0.9)',
                            transform: 'translateY(-2px)'
                        },
                        transition: 'all 0.3s ease'
                    }}
                    startIcon={<ArrowBackIcon />}
                    onClick={() => navigate(-1)}
                >
                    Back
                </Button>
            </Box>
            </Box>
        </Paper>
        </Slide>
        
        {/* Main Content */}
        <Grid container spacing={4}>
        {/* Tabs Section */}
        <Grid item xs={12} md={8}>
            <Card 
            elevation={2} 
            sx={{ 
                borderRadius: 3,
                overflow: 'hidden'
            }}
            >
            <CardContent sx={{ p: 0 }}>
                <Tabs
                value={tabValue}
                onChange={handleTabChange}
                variant={isMobile ? 'scrollable' : 'standard'}
                scrollButtons="auto"
                allowScrollButtonsMobile
                sx={{
                    px: 3,
                    pt: 2,
                    '& .MuiTab-root': {
                    minHeight: 60,
                    textTransform: 'none',
                    fontSize: '1rem',
                    fontWeight: 500,
                    '&.Mui-selected': {
                        color: theme.palette.secondary.main,
                    }
                    },
                    '& .MuiTabs-indicator': {
                        backgroundColor: theme.palette.secondary.main
                    }
                }}
                >
                <Tab icon={<Person />} iconPosition="start" label="Researcher" />
                <Tab icon={<Description />} iconPosition="start" label="Description" />
                <Tab icon={<Science />} iconPosition="start" label="Specifications" />
                <Tab icon={<Verified />} iconPosition="start" label="Technical" />
                </Tabs>
                
                <Divider />

                <Box sx={{ p: 3 }}>
                <AnimatePresence mode="wait">
                    {tabValue === 0 && (
                    <motion.div
                        key="researcher"
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        exit={{ opacity: 0, y: -20 }}
                        transition={{ duration: 0.3 }}
                    >
                        <Box sx={{ textAlign: 'center', mb: 4 }}>
                        <Avatar
                            sx={{
                            width: 120,
                            height: 120,
                            mx: 'auto',
                            mb: 3,
                            bgcolor: 'secondary.main',
                            fontSize: '3rem'
                            }}
                        >
                            <Person sx={{ fontSize: 48 }} />
                        </Avatar>
                        
                        <Typography variant="h4" gutterBottom sx={{ fontWeight: 'bold' }}>
                            {getIdFullName(researcherName, researchersData) || 'Researcher Name'}
                        </Typography>
                        
                        <Typography variant="h6" color="secondary" gutterBottom>
                            Product Developer
                        </Typography>
                        
                        <Chip
                            icon={<Business />}
                            label={getIdCategory(universityName, universitiesData) || 'University'}
                            color="secondary"
                            variant="outlined"
                            sx={{ mt: 1 }}
                        />
                        </Box>

                        <Grid container spacing={3} sx={{ mt: 2 }}>
                        {researcherInfo?.email && (
                            <Grid item xs={12} sm={6}>
                            <Card variant="outlined" sx={{ p: 2, textAlign: 'center' }}>
                                <Email color="secondary" sx={{ fontSize: 40, mb: 1 }} />
                                <Typography variant="subtitle2" color="text.secondary">
                                Email
                                </Typography>
                                <Typography variant="body2">
                                {researcherInfo.email}
                                </Typography>
                            </Card>
                            </Grid>
                        )}
                        
                        {researcherInfo?.phone && (
                            <Grid item xs={12} sm={6}>
                            <Card variant="outlined" sx={{ p: 2, textAlign: 'center' }}>
                                <Phone color="secondary" sx={{ fontSize: 40, mb: 1 }} />
                                <Typography variant="subtitle2" color="text.secondary">
                                Phone
                                </Typography>
                                <Typography variant="body2">
                                {researcherInfo.phone}
                                </Typography>
                            </Card>
                            </Grid>
                        )}

                        {(!researcherInfo?.email && !researcherInfo?.phone) && (
                            <Grid item xs={12}>
                            <Card variant="outlined" sx={{ p: 3, textAlign: 'center' }}>
                                <Typography variant="body1" color="text.secondary">
                                Contact information not available for this researcher.
                                </Typography>
                            </Card>
                            </Grid>
                        )}
                        </Grid>
                    </motion.div>
                    )}

                    {tabValue === 1 && (
                    <motion.div
                        key="description"
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        exit={{ opacity: 0, y: -20 }}
                        transition={{ duration: 0.3 }}
                    >
                        <Typography variant="h5" gutterBottom sx={{ fontWeight: 'bold', color: 'secondary.main' }}>
                        Product Description
                        </Typography>
                        
                        <Paper 
                        elevation={0} 
                        sx={{ 
                            p: 3, 
                            backgroundColor: 'grey.50',
                            borderRadius: 2,
                            border: `1px solid ${theme.palette.divider}`
                        }}
                        >
                        <Typography 
                            variant="body1" 
                            sx={{ 
                            lineHeight: 1.8,
                            fontSize: '1.1rem',
                            whiteSpace: 'pre-line'
                            }}
                        >
                            {productDescription || 'No description available for this product.'}
                        </Typography>
                        </Paper>

                        <Box sx={{ mt: 4 }}>
                        <Typography variant="h6" gutterBottom sx={{ fontWeight: 'bold' }}>
                            Product Overview
                        </Typography>
                        <List>
                            <ListItem>
                            <ListItemIcon>
                                <Science color="secondary" />
                            </ListItemIcon>
                            <ListItemText 
                                primary="Research-Based"
                                secondary="Developed through academic research and innovation"
                            />
                            </ListItem>
                            <ListItem>
                            <ListItemIcon>
                                <Groups color="secondary" />
                            </ListItemIcon>
                            <ListItemText 
                                primary="Academic Product"
                                secondary="Created within university research environment"
                            />
                            </ListItem>
                        </List>
                        </Box>
                    </motion.div>
                    )}

                    {tabValue === 2 && (
                    <motion.div
                        key="specifications"
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        exit={{ opacity: 0, y: -20 }}
                        transition={{ duration: 0.3 }}
                    >
                        <Typography variant="h5" gutterBottom sx={{ fontWeight: 'bold', color: 'secondary.main' }}>
                        Product Specifications
                        </Typography>
                        
                        {parsedProductDetails.length > 0 ? (
                        parsedProductDetails.map((item, index) => (
                            <motion.div
                            key={index}
                            initial={{ opacity: 0, x: -20 }}
                            animate={{ opacity: 1, x: 0 }}
                            transition={{ delay: index * 0.1 }}
                            >
                            <Card 
                                variant="outlined" 
                                sx={{ 
                                mb: 2,
                                p: 2,
                                borderRadius: 2,
                                borderLeft: `4px solid ${theme.palette.secondary.main}`
                                }}
                            >
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                                <Avatar sx={{ bgcolor: 'secondary.main', width: 40, height: 40 }}>
                                    <Typography variant="body2" fontWeight="bold" color="white">
                                    {index + 1}
                                    </Typography>
                                </Avatar>
                                <Typography variant="body1" sx={{ flex: 1 }}>
                                    {item.data}
                                </Typography>
                                </Box>
                            </Card>
                            </motion.div>
                        ))
                        ) : (
                        <Paper 
                            elevation={0} 
                            sx={{ 
                            p: 4, 
                            textAlign: 'center',
                            backgroundColor: 'grey.50',
                            borderRadius: 2
                            }}
                        >
                            <Description sx={{ fontSize: 48, color: 'text.secondary', mb: 2 }} />
                            <Typography variant="h6" color="text.secondary" gutterBottom>
                            No Product Specifications Available
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                            Detailed specifications for this product have not been provided.
                            </Typography>
                        </Paper>
                        )}
                    </motion.div>
                    )}

                    {tabValue === 3 && (
                    <motion.div
                        key="technical"
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        exit={{ opacity: 0, y: -20 }}
                        transition={{ duration: 0.3 }}
                    >
                        <Typography variant="h5" gutterBottom sx={{ fontWeight: 'bold', color: 'secondary.main' }}>
                        Technical & Legal Information
                        </Typography>
                        
                        {hasTechnicalDetails() ? (
                            <Stack spacing={2}>
                                {composition && (
                                    <Card variant="outlined" sx={{ borderRadius: 2 }}>
                                        <CardHeader
                                            avatar={<Science sx={{ color: 'secondary.main' }} />}
                                            title="Composition"
                                            titleTypographyProps={{ variant: 'h6', fontWeight: 'bold' }}
                                        />
                                        <CardContent>
                                            <Typography variant="body1" sx={{ whiteSpace: 'pre-line' }}>
                                                {composition}
                                            </Typography>
                                        </CardContent>
                                    </Card>
                                )}
                                
                                {manufacturer && (
                                    <Card variant="outlined" sx={{ borderRadius: 2 }}>
                                        <CardHeader
                                            avatar={<Factory sx={{ color: 'secondary.main' }} />}
                                            title="Manufacturer"
                                            titleTypographyProps={{ variant: 'h6', fontWeight: 'bold' }}
                                        />
                                        <CardContent>
                                            <Typography variant="body1">
                                                {manufacturer}
                                            </Typography>
                                        </CardContent>
                                    </Card>
                                )}
                                
                                {tradeMark && (
                                    <Card variant="outlined" sx={{ borderRadius: 2 }}>
                                        <CardHeader
                                            avatar={<TramRounded sx={{ color: 'secondary.main' }} />}
                                            title="Trade Mark"
                                            titleTypographyProps={{ variant: 'h6', fontWeight: 'bold' }}
                                        />
                                        <CardContent>
                                            <Typography variant="body1">
                                                {tradeMark}
                                            </Typography>
                                        </CardContent>
                                    </Card>
                                )}
                                
                                {patent && (
                                    <Card variant="outlined" sx={{ borderRadius: 2 }}>
                                        <CardHeader
                                            avatar={<Verified sx={{ color: 'secondary.main' }} />}
                                            title="Patent Information"
                                            titleTypographyProps={{ variant: 'h6', fontWeight: 'bold' }}
                                        />
                                        <CardContent>
                                            <Typography variant="body1" sx={{ whiteSpace: 'pre-line' }}>
                                                {patent}
                                            </Typography>
                                        </CardContent>
                                    </Card>
                                )}
                            </Stack>
                        ) : (
                            <Paper 
                                elevation={0} 
                                sx={{ 
                                    p: 4, 
                                    textAlign: 'center',
                                    backgroundColor: 'grey.50',
                                    borderRadius: 2
                                }}
                            >
                                <Verified sx={{ fontSize: 48, color: 'text.secondary', mb: 2 }} />
                                <Typography variant="h6" color="text.secondary" gutterBottom>
                                    No Technical Information Available
                                </Typography>
                                <Typography variant="body2" color="text.secondary">
                                    Technical specifications and legal information have not been provided for this product.
                                </Typography>
                            </Paper>
                        )}
                    </motion.div>
                    )}
                </AnimatePresence>
                </Box>
            </CardContent>
            </Card>
        </Grid>

        {/* Sidebar */}
        <Grid item xs={12} md={4}>
            <Stack spacing={3}>
            {/* Quick Stats */}
            <Card elevation={2} sx={{ borderRadius: 3 }}>
                <CardHeader
                title="Product Information"
                titleTypographyProps={{ variant: 'h6', fontWeight: 'bold' }}
                />
                <CardContent>
                <Stack spacing={2}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <Typography variant="body2" color="text.secondary">
                        Published Date
                    </Typography>
                    <Typography variant="body2" fontWeight="bold">
                        {uploadDate || 'N/A'}
                    </Typography>
                    </Box>
                    
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <Typography variant="body2" color="text.secondary">
                        Specifications
                    </Typography>
                    <Typography variant="body2" fontWeight="bold">
                        {parsedProductDetails.length} items
                    </Typography>
                    </Box>

                    {manufacturer && (
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                            <Typography variant="body2" color="text.secondary">
                                Manufacturer
                            </Typography>
                            <Typography variant="body2" fontWeight="bold">
                                {manufacturer.length > 20 ? manufacturer.substring(0, 20) + '...' : manufacturer}
                            </Typography>
                        </Box>
                    )}

                    {tradeMark && (
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                            <Typography variant="body2" color="text.secondary">
                                Trade Mark
                            </Typography>
                            <Chip 
                                label={tradeMark} 
                                size="small" 
                                variant="outlined"
                                icon={<TramRounded />}
                            />
                        </Box>
                    )}
                    
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <Typography variant="body2" color="text.secondary">
                        Status
                    </Typography>
                    <Chip 
                        label="Active" 
                        color="success" 
                        size="small" 
                    />
                    </Box>

                    {patent && (
                        <Box>
                            <Divider sx={{ my: 1 }} />
                            <Typography variant="body2" color="text.secondary" gutterBottom>
                                Patent Status
                            </Typography>
                            <Chip 
                                icon={<Verified />}
                                label="Patent Protected" 
                                size="small" 
                                color="info"
                                variant="outlined"
                            />
                        </Box>
                    )}
                </Stack>
                </CardContent>
            </Card>

            {/* University Info */}
            <Card elevation={2} sx={{ borderRadius: 3 }}>
                <CardHeader
                avatar={<School color="secondary" />}
                title="Research Institution"
                titleTypographyProps={{ variant: 'h6', fontWeight: 'bold' }}
                />
                <CardContent>
                <Typography variant="body1" gutterBottom fontWeight="medium">
                    {getIdCategory(universityName, universitiesData) || 'University'}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                    Academic institution dedicated to research and innovation.
                </Typography>
                </CardContent>
            </Card>

            {/* Researcher Contact */}
            {researcherInfo && (
                <Card elevation={2} sx={{ borderRadius: 3 }}>
                <CardHeader
                    avatar={<Person color="secondary" />}
                    title="Contact Researcher"
                    titleTypographyProps={{ variant: 'h6', fontWeight: 'bold' }}
                />
                <CardContent>
                    <Typography variant="body2" gutterBottom>
                    {getIdFullName(researcherName, researchersData)}
                    </Typography>
                    {researcherInfo.email && (
                    <Button
                        variant="text"
                        startIcon={<Email />}
                        fullWidth
                        sx={{ justifyContent: 'flex-start', mt: 1, color: 'secondary.main' }}
                        onClick={() => window.location.href = `mailto:${researcherInfo.email}`}
                    >
                        {researcherInfo.email}
                    </Button>
                    )}
                    {!researcherInfo.email && (
                        <Typography variant="body2" color="text.secondary">
                            No contact information available
                        </Typography>
                    )}
                </CardContent>
                </Card>
            )}
            </Stack>
        </Grid>
        </Grid>
    </Container>
    </Box>
);
};

export default ProductDetails;